import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getClients } from 'services/clients';
import { Client, RowClient } from 'types/client';
import { SortProps } from 'types/tables';

export const useClientsHook = ({
  page = 1,
  limit = 10,
  sort,
}: {
  page?: number;
  limit?: number;
  sort?: SortProps<Client>;
} = {}) => {
  const [clients, setClients] = useState<RowClient[] | Client[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const {
    data,
    isLoading: loading,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: ['FETCH-CLIENTS', page],
    queryFn: () =>
      getClients({
        page,
        limit,
        sort,
      }),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isSuccess && data) {
      setClients(data.items);
      setTotalPages(data.totalPages);
    }
  }, [isSuccess, data]);

  const sortDataBy = async (sort: SortProps<Client>) => {
    if (sort) {
      const sortedItems = clients.slice().sort((a, b) => {
        const factor = sort.order === 'asc' ? -1 : 1;
        const field = sort.field as keyof Client;
        const A = a[field];
        const B = b[field];
        if (A < B) return factor * -1;
        if (A > B) return factor * 1;
        return 0;
      });
      setClients(sortedItems);
    }
  };

  const checkAllElements = () => {
    setClients((items) => {
      return items.map((item) => ({
        ...item,
        isChecked: !isAllChecked,
      }));
    });

    setIsAllChecked((prev) => !prev);
  };

  return {
    clients,
    loading,
    sortDataBy,
    checkAllElements,
    totalPages,
    refetch,
  };
};
